/**
 * Methods for interacting with ToDesktop application's menu.
 *
 * @remarks
 * This package exposes a number of methods for interacting with ToDesktop
 * application's menu.

 * @experimental
 * @public
 * @packageDocumentation
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { construct, invoke } from "./invoke.js";
import { createOnAndOffNamespaceListeners, } from "./utils.js";
/**
 * Creates a new Menu.
 *
 * @returns Identifier for the newly created Menu.
 * @public
 */
export async function create() {
    return construct({ namespace: "Menu", args: [] });
}
/**
 * Pops up this menu as a context menu in the BrowserWindow.
 *
 * @param options - ref: Reference for the Menu.
 * @public
 */
export async function popup(_a) {
    var { ref } = _a, options = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "Menu",
        method: "popup",
        args: [options],
    });
}
/**
 * Closes the context menu in the browserWindow.
 *
 * @param options - ref: Reference for the Menu.
 * @public
 */
export async function closePopup({ ref, browserWindow, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Menu",
        method: "closePopup",
        args: [browserWindow],
    });
}
/**
 * Appends the menuItem to the menu.
 *
 * @param options - ref: Reference for the Menu.
 * @public
 */
export async function append({ ref, menuItem, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Menu",
        method: "append",
        args: [menuItem],
    });
}
/**
 * Inserts the menuItem to the pos position of the menu.
 *
 * @param options - ref: Reference for the Menu.
 * @public
 */
export async function insert({ ref, menuItem, pos, }) {
    return invoke({
        instanceRef: ref,
        namespace: "Menu",
        method: "insert",
        args: [pos, menuItem],
    });
}
/**
 * Gets the item with the specified id
 *
 * @param options - ref: Reference for the Menu; id: string id of the menu.
 * @public
 */
export async function getMenuItemById({ ref, id }) {
    return invoke({
        instanceRef: ref,
        namespace: "Menu",
        method: "getMenuItemById",
        args: [id],
    });
}
/**
 * Sets menu as the application menu on macOS. On Windows and Linux, the menu will be set as each window's top menu.
 *
 * @param options - menu: Reference for the Menu.
 * @public
 */
export async function setApplicationMenu({ menu }) {
    return invoke({
        namespace: "Menu",
        method: "setApplicationMenu",
        args: [menu],
    });
}
/**
 * The application menu, if set, or null, if not set.
 *
 * @public
 */
export async function getApplicationMenu() {
    return invoke({
        namespace: "Menu",
        method: "getApplicationMenu",
        args: [],
    });
}
/**
 * Sends the action to the first responder of application. This is used for emulating default macOS menu behaviors. Usually you would use the role property of a MenuItem. MacOS only.
 *
 * @param options - action: string.
 * @public
 */
export async function sendActionToFirstResponder({ action, }) {
    return invoke({
        namespace: "Menu",
        method: "sendActionToFirstResponder",
        args: [action],
    });
}
/**
 * Build a new menu from a template of options
 *
 * @param options - template: array of options for constructing a MenuItem.
 * @public
 */
export async function buildFromTemplate({ template, }) {
    return invoke({
        namespace: "Menu",
        method: "buildFromTemplate",
        // @ts-expect-error - using custom menu item options to avoid difficulty with our automatic type conversion
        args: [template],
    });
}
const listeners = createOnAndOffNamespaceListeners("Menu");
/**
 * Subcribes to an event on a menu object.
 *
 * @param eventName - The event name to subscribe to.
 * @param callback - The callback function to execute when the event occurs.
 * @param options - ref: Reference for the Menu.
 * @returns unsubscribe callback.
 * @public
 */
export const on = listeners.on;
/**
 * Unsubscribes all menu objects from the event name.
 *
 * @param options - ref: Reference for the Menu; eventName: The event name to unsubscribe from;
 * @returns Reference for the menu.
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
